import { PosVendaPeriodoCampo, PosVendaProtocoloStatus, PosVendaProtocolosFiltros } from '../../types/posVenda';
import { podeTransicionarStatus } from './posVendaStatusRules';
import { PROTOCOLO_STATUS_LABEL } from './posVendaLabels';

/** Tamanho de página por coluna no kanban (listagem paginada por status). */
export const KANBAN_COLUMN_PAGE_SIZE = 30;

/** Colunas de protocolos encerrados — recolhidas por padrão no quadro. */
export const KANBAN_COLUNAS_FECHADAS: PosVendaProtocoloStatus[] = ['Concluido', 'Cancelado'];

/** Ordem alinhada ao enum numérico da API (`PosVendaProtocoloStatus`). */
export const KANBAN_STATUS_API_NUMERIC: Record<PosVendaProtocoloStatus, number> = {
  Aberto: 0,
  EmAnalise: 1,
  AguardandoCliente: 2,
  AguardandoTerceiro: 3,
  EmExecucao: 4,
  Concluido: 5,
  Cancelado: 6,
};

/** Colunas do kanban (ordem de fluxo). */
export const KANBAN_COLUMNS: PosVendaProtocoloStatus[] = [
  'Aberto',
  'EmAnalise',
  'AguardandoCliente',
  'AguardandoTerceiro',
  'EmExecucao',
  'Concluido',
  'Cancelado',
];

export function kanbanColumnLabel(status: PosVendaProtocoloStatus): string {
  return PROTOCOLO_STATUS_LABEL[statusCanonico(status)];
}

/** A API serializa o enum em camelCase (`emAnalise`). Regras e rótulos usam PascalCase. */
export function statusCanonico(status: string): PosVendaProtocoloStatus {
  const hit = KANBAN_COLUMNS.find((col) => col.toLowerCase() === status.toLowerCase());
  return hit ?? 'Aberto';
}

export function podeMoverKanban(
  de: PosVendaProtocoloStatus,
  para: PosVendaProtocoloStatus,
  opts?: { gestorReabre?: boolean; gestorLivre?: boolean }
): boolean {
  if (de === para) return false;
  if (opts?.gestorLivre) return true;
  return podeTransicionarStatus(de, para, opts);
}

export function statusFromApiNumeric(value: number): PosVendaProtocoloStatus | null {
  const hit = (Object.entries(KANBAN_STATUS_API_NUMERIC) as [PosVendaProtocoloStatus, number][]).find(
    ([, n]) => n === value
  );
  return hit?.[0] ?? null;
}

export function isKanbanColunaFechada(status: PosVendaProtocoloStatus): boolean {
  return KANBAN_COLUNAS_FECHADAS.includes(status);
}

function dataCivilHojeSaoPaulo(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function addDaysToYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const utc = Date.UTC(y, m - 1, d + deltaDays);
  return new Date(utc).toISOString().slice(0, 10);
}

/** Período padrão ao expandir colunas Concluído/Cancelado: conclusão nos últimos 7 dias (SP). */
export function periodoConclusaoUltimos7Dias(): Pick<
  PosVendaProtocolosFiltros,
  'periodoCampo' | 'periodoDe' | 'periodoAte' | 'periodoDeUtc' | 'periodoAteUtc' | 'from' | 'to'
> {
  const ate = dataCivilHojeSaoPaulo();
  const de = addDaysToYmd(ate, -6);
  return {
    periodoCampo: 'conclusao' satisfies PosVendaPeriodoCampo,
    periodoDe: de,
    periodoAte: ate,
    periodoDeUtc: undefined,
    periodoAteUtc: undefined,
    from: undefined,
    to: undefined,
  };
}

/** Initial view: today and 29 previous civil days, with all open work preserved. */
export function periodoPadraoProtocolos(): PosVendaProtocolosFiltros {
  const ate = dataCivilHojeSaoPaulo();
  return { periodoCampo: 'insercao', periodoDe: addDaysToYmd(ate, -29), periodoAte: ate, incluirAbertosForaPeriodo: true };
}

/** Conclusão no dia de hoje (America/Sao_Paulo) — mesmo preset do card KPI Concluídos hoje. */
export function periodoConclusaoHoje(): Pick<
  PosVendaProtocolosFiltros,
  'periodoCampo' | 'periodoDe' | 'periodoAte' | 'periodoDeUtc' | 'periodoAteUtc' | 'from' | 'to'
> {
  const hoje = dataCivilHojeSaoPaulo();
  return {
    periodoCampo: 'conclusao' satisfies PosVendaPeriodoCampo,
    periodoDe: hoje,
    periodoAte: hoje,
    periodoDeUtc: undefined,
    periodoAteUtc: undefined,
    from: undefined,
    to: undefined,
  };
}

export function isFiltroConcluidosHoje(filtros: PosVendaProtocolosFiltros): boolean {
  if (statusCanonico(filtros.status ?? '') !== 'Concluido') return false;
  if ((filtros.periodoCampo ?? 'insercao') !== 'conclusao') return false;
  const hoje = dataCivilHojeSaoPaulo();
  const de = (filtros.periodoDe ?? filtros.from ?? '').slice(0, 10);
  const ate = (filtros.periodoAte ?? filtros.to ?? '').slice(0, 10);
  return de === hoje && ate === hoje;
}

export function buildKanbanColumnFiltros(
  base: PosVendaProtocolosFiltros,
  column: PosVendaProtocoloStatus,
  opts: { colunaFechadaExpandida: boolean }
): PosVendaProtocolosFiltros {
  const { page: _p, pageSize: _s, status: _st, ...shared } = base;
  if (isKanbanColunaFechada(column) && opts.colunaFechadaExpandida) {
    return {
      ...shared,
      status: column,
      pageSize: KANBAN_COLUMN_PAGE_SIZE,
      ...periodoConclusaoUltimos7Dias(),
      incluirAbertosForaPeriodo: false,
    };
  }
  return {
    ...shared,
    status: column,
    pageSize: KANBAN_COLUMN_PAGE_SIZE,
  };
}

export function mapContagensPorStatus(
  contagens: Array<{ status: number | string; count: number }>
): Map<PosVendaProtocoloStatus, number> {
  const map = new Map<PosVendaProtocoloStatus, number>();
  contagens.forEach(({ status, count }) => {
    const col = typeof status === 'number'
      ? statusFromApiNumeric(status)
      : KANBAN_COLUMNS.find((column) => column.toLowerCase() === status.toLowerCase());
    if (col && count > 0) map.set(col, count);
  });
  return map;
}

/** Soma contagens por status (ex.: agregar raias do kanban por pessoa). */
export function mergeContagensPorStatus(
  maps: Array<Map<PosVendaProtocoloStatus, number>>
): Map<PosVendaProtocoloStatus, number> {
  const merged = new Map<PosVendaProtocoloStatus, number>();
  for (const col of KANBAN_COLUMNS) {
    let sum = 0;
    for (const m of maps) sum += m.get(col) ?? 0;
    if (sum > 0) merged.set(col, sum);
  }
  return merged;
}

export function filtrosContagensSemStatusPaginacao(
  filtros: PosVendaProtocolosFiltros
): PosVendaProtocolosFiltros {
  const { page: _p, pageSize: _s, status: _st, ...rest } = filtros;
  return rest;
}
